import { mintEntityId } from "@repo/domain";
import { cn } from "@repo/ui/lib/utils";
import { Extension } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionMatch, type Trigger } from "@tiptap/suggestion";

interface PhraseItem {
  trigger: string;
  label: string;
  insert: () => { type: string; attrs: Record<string, unknown> };
}

const PHRASES: readonly PhraseItem[] = [
  {
    trigger: "med",
    label: "Medication",
    insert: () => ({ type: "medicationPhrase", attrs: { statementId: mintEntityId(), autofocus: true } }),
  },
];

/**
 * "." after a space or at a line's start, alone (every phrase) or with the
 * start of one (".me"). A sentence's period never matches, because the dot must
 * start a word; a decimal (" .5") closes the list at its first digit.
 */
function findPhraseMatch({ $position }: Trigger): SuggestionMatch {
  const before = $position.parent.textBetween(0, $position.parentOffset, "", "￼");
  const match = /(?:^|\s)(\.[a-zA-Z]*)$/.exec(before);
  const text = match?.[1];
  if (!text) return null;
  return { range: { from: $position.pos - text.length, to: $position.pos }, query: text.slice(1), text };
}

const matching = (query: string) => PHRASES.filter((phrase) => phrase.trigger.startsWith(query.toLowerCase()));

function PhraseMenu({
  items,
  active,
  choose,
}: {
  items: PhraseItem[];
  active: number;
  choose: (item: PhraseItem) => void;
}) {
  return (
    <div role="listbox" className="min-w-48 rounded-md border bg-popover p-1 text-popover-foreground text-sm shadow-md">
      {items.map((item, index) => (
        // biome-ignore lint/a11y/useFocusableInteractive: focus stays in the editor so typing keeps narrowing the list.
        <div
          key={item.trigger}
          role="option"
          aria-selected={index === active}
          className={cn(
            "cursor-pointer rounded-sm px-2 py-1.5",
            index === active && "bg-accent text-accent-foreground",
          )}
          onMouseDown={(event) => {
            event.preventDefault();
            choose(item);
          }}
        >
          <span className="font-medium">.{item.trigger}</span>{" "}
          <span className="text-muted-foreground">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

const SmartPhrasePluginKey = new PluginKey("smartPhrase");

/** Typing "." lists the phrases, ".med" narrows them; Enter or Tab replaces the trigger with the pick. */
export const SmartPhraseTrigger = Extension.create({
  name: "smartPhraseTrigger",
  // Ahead of the slash menu and the list keymaps, so Enter picks the phrase rather than splitting the line.
  priority: 105,
  addProseMirrorPlugins() {
    return [
      Suggestion<PhraseItem>({
        editor: this.editor,
        pluginKey: SmartPhrasePluginKey,
        char: ".",
        findSuggestionMatch: findPhraseMatch,
        items: ({ query }) => matching(query),
        shouldShow: ({ query }) => matching(query).length > 0,
        // A phrase gets a line of its own: text typed before or after the trigger stays on its own lines.
        command: ({ editor, range, props }) => {
          const $from = editor.state.doc.resolve(range.from);
          // A phrase already on the line counts as content: it has no text of its own.
          const LEAF = "\uFFFC";
          const before = $from.parent.textBetween(0, $from.parentOffset, undefined, LEAF).trim();
          const after = $from.parent.textBetween(
            $from.parentOffset + (range.to - range.from),
            $from.parent.content.size,
            undefined,
            LEAF,
          );
          const chain = editor.chain().focus().deleteRange(range);
          if (before) chain.splitBlock();
          chain.insertContent(props.insert());
          if (after.trim()) chain.splitBlock();
          chain.run();
        },
        render: () => {
          let component: ReactRenderer<unknown, Parameters<typeof PhraseMenu>[0]> | undefined;
          let popup: HTMLDivElement | undefined;
          let items: PhraseItem[] = [];
          let active = 0;
          let choose: (item: PhraseItem) => void = () => undefined;
          let rect: (() => DOMRect | null) | null | undefined;
          const place = () => {
            const caret = rect?.();
            if (!popup || !caret) return;
            popup.style.top = `${caret.bottom + 4}px`;
            popup.style.left = `${caret.left}px`;
          };
          const render = () => {
            component?.updateProps({ items, active, choose });
            place();
          };
          return {
            onStart(props) {
              items = props.items;
              active = 0;
              choose = props.command;
              rect = props.clientRect;
              component = new ReactRenderer(PhraseMenu, { editor: props.editor, props: { items, active, choose } });
              popup = document.createElement("div");
              popup.style.cssText = "position:fixed;z-index:50";
              popup.appendChild(component.element);
              document.body.appendChild(popup);
              place();
            },
            onUpdate(props) {
              items = props.items;
              active = 0;
              choose = props.command;
              rect = props.clientRect;
              render();
            },
            onKeyDown({ event }) {
              if (!items.length) return false;
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                active = (active + (event.key === "ArrowUp" ? -1 : 1) + items.length) % items.length;
                render();
                return true;
              }
              if (event.key === "Enter" || event.key === "Tab") {
                const item = items[active];
                if (item) choose(item);
                return true;
              }
              return false;
            },
            onExit() {
              popup?.remove();
              component?.destroy();
              popup = undefined;
              component = undefined;
            },
          };
        },
      }),
    ];
  },
});
